/**
 * This component catches all errors in the render tree it surrounds.  It should
 * only be used a last resort when all other error handling fails.
 *
 * TODO(kevinb): move this into a shared component once we decide what to do
 * about shipping error boundaries.
 */
import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {color} from "@khanacademy/wonder-blocks-tokens";
import {HeadingMedium} from "@khanacademy/wonder-blocks-typography";
import {doNotTranslate} from "@khan/i18n";
import Log, {Errors} from "@khan/logging";
import {RequestInfo} from "@khan/shared-core";

type State = {
    error: Error | null | undefined;
    errorInfo: any | null | undefined;
};

type Props = {
    children: React.ReactNode;
    style?: any;
};

export default class ErrorBoundary extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {error: null, errorInfo: null};
    }

    componentDidCatch(error: Error, errorInfo: any) {
        // Catch errors in any components below and re-render with error message
        this.setState({
            error: error,
            errorInfo: errorInfo,
        });
        // Also log error messages to our error reporting service here
        Log.error(error, Errors.Internal, {
            sentryData: {
                contexts: {extras: errorInfo},
            },
        });
    }

    render(): React.ReactNode {
        if (this.state.errorInfo) {
            const {style} = this.props;

            // Error path
            if (RequestInfo.IS_DEV_SERVER) {
                return (
                    <View style={[style, styles.stackTraceContainer]}>
                        <HeadingMedium>
                            {doNotTranslate`Something went wrong.`}
                        </HeadingMedium>
                        <details style={{whiteSpace: "pre-wrap"}}>
                            {this.state.error && this.state.error.toString()}
                            {this.state.errorInfo &&
                                this.state.errorInfo.componentStack}
                        </details>
                    </View>
                );
            }
            // TODO(kevinb): provide an actual error message
            return null;
        }
        // Normally, just render children
        return this.props.children;
    }
}

const styles = StyleSheet.create({
    stackTraceContainer: {
        padding: 20,
        border: `solid 1px ${color.red}`,
        borderRadius: 4,
        backgroundColor: color.fadedRed24,
    },
});
